import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, RefreshCw, X, Save, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_VENUE = {
  name: "",
  category: "",
  city: "",
  description: "",
  best_for: "",
  capacity: "",
  io: "Indoor & Outdoor",
  budget: "Premium",
  image: "",
  extra_images: [],
  features: [],
  cta_text: "Find Venues For My Event",
  active: true,
  sort_order: 100,
};

const IO_OPTS = ["Indoor", "Outdoor", "Indoor & Outdoor"];
const BUDGET_OPTS = ["Mid", "Mid – Premium", "Premium", "Premium – Luxury", "Luxury"];

export default function AdminVenuesPanel({ token }) {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // venue being edited (or 'new')
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/venues`, { headers });
      setVenues(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Could not load venues");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ ...EMPTY_VENUE, sort_order: (venues.length + 1) * 10 });
  const startEdit = (v) => setEditing({ ...EMPTY_VENUE, ...v });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    if (!editing.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      // Strip server-managed fields before posting
      const { id, created_at, updated_at, ...payload } = editing;
      payload.sort_order = Number(payload.sort_order) || 100;
      payload.extra_images = Array.isArray(payload.extra_images) ? payload.extra_images : [];
      payload.features = Array.isArray(payload.features) ? payload.features : [];

      if (id) {
        await axios.put(`${API}/admin/venues/${id}`, payload, { headers });
        toast.success("Venue updated");
      } else {
        await axios.post(`${API}/admin/venues`, payload, { headers });
        toast.success("Venue created");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete venue "${v.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/venues/${v.id}`, { headers });
      toast.success("Venue deleted");
      setVenues((prev) => prev.filter((x) => x.id !== v.id));
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const toggleActive = async (v) => {
    try {
      const { id, created_at, updated_at, ...payload } = v;
      payload.active = !v.active;
      await axios.put(`${API}/admin/venues/${v.id}`, payload, { headers });
      toast.success(payload.active ? "Venue published" : "Venue hidden");
      await load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="admin-venues-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <p className="text-[13px] text-[#3B312D]/70">{venues.length} venue{venues.length === 1 ? "" : "s"} · {venues.filter((v) => v.active).length} live</p>
        </div>
        <div className="flex gap-3">
          <button onClick={load} disabled={loading} className="btn-ghost" data-testid="admin-venues-refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {loading ? "Loading…" : "Refresh"}
          </button>
          <button onClick={startNew} className="btn-primary" data-testid="admin-venues-new">
            <Plus size={14} /> Add venue
          </button>
        </div>
      </div>

      {/* List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {venues.length === 0 && !loading && (
          <p className="text-[#3B312D]/60 py-12">No venues yet — add your first one to get started.</p>
        )}
        {venues.map((v) => (
          <div key={v.id} data-testid={`admin-venue-${v.id}`} className={`lux-card p-5 flex gap-4 ${!v.active ? "opacity-70" : ""}`}>
            <div className="w-28 h-28 rounded-xl overflow-hidden shrink-0 bg-[#F4DCD8]/40">
              {v.image ? (
                <img src={v.image} alt={v.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#3B312D]/40 text-xs">No image</div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-display text-xl text-[#3B312D] truncate">{v.name}</h3>
                  <p className="text-[11px] tracking-[0.2em] uppercase text-[#8A9A7B] font-semibold mt-1">
                    {v.category || "—"} {v.city ? `· ${v.city}` : ""}
                  </p>
                </div>
                <span className={`pill !text-[10px] !py-1 !px-3 ${v.active ? "" : "!bg-[#3B312D]/10 !border-[#3B312D]/20 !text-[#3B312D]/60"}`}>
                  {v.active ? "Live" : "Hidden"}
                </span>
              </div>
              <p className="mt-2 text-[13px] text-[#3B312D]/75 line-clamp-2">{v.best_for || v.description || "—"}</p>
              <div className="mt-2 grid grid-cols-3 gap-2 text-[11.5px] text-[#3B312D]/80">
                <div><span className="text-[#8A9A7B]">Cap:</span> {v.capacity || "—"}</div>
                <div><span className="text-[#8A9A7B]">I/O:</span> {v.io || "—"}</div>
                <div><span className="text-[#8A9A7B]">Tier:</span> {v.budget || "—"}</div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => startEdit(v)} className="btn-ghost !py-1.5 !px-3 !text-[11px]" data-testid={`admin-venue-edit-${v.id}`}>
                  <Edit2 size={12} /> Edit
                </button>
                <button onClick={() => toggleActive(v)} className="btn-ghost !py-1.5 !px-3 !text-[11px]" data-testid={`admin-venue-toggle-${v.id}`}>
                  {v.active ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Publish</>}
                </button>
                <button onClick={() => remove(v)} className="text-[11px] tracking-[0.18em] uppercase text-[#B65980] hover:underline font-semibold inline-flex items-center gap-1.5" data-testid={`admin-venue-delete-${v.id}`}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-[#3B312D]/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
          onClick={cancel}
          data-testid="admin-venue-modal"
        >
          <div
            className="bg-[#FAF6F1] rounded-2xl w-full max-w-2xl my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-7 py-5 border-b border-[rgba(59,49,45,0.08)]">
              <h3 className="font-display text-2xl text-[#3B312D]">
                {editing.id ? "Edit venue" : "New venue"}
              </h3>
              <button onClick={cancel} className="text-[#3B312D]/60 hover:text-[#B65980]" data-testid="admin-venue-modal-close">
                <X size={20} />
              </button>
            </div>
            <div className="px-7 py-6 space-y-4">
              <Field label="Name *">
                <input className="lux-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="admin-venue-field-name" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Category">
                  <input className="lux-input" placeholder="Hotel, Resort, Banquet…" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="admin-venue-field-category" />
                </Field>
                <Field label="City">
                  <input className="lux-input" placeholder="Mumbai, Udaipur…" value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} data-testid="admin-venue-field-city" />
                </Field>
              </div>
              <Field label="Best for">
                <input className="lux-input" placeholder="Weddings, receptions, conferences…" value={editing.best_for} onChange={(e) => setEditing({ ...editing, best_for: e.target.value })} data-testid="admin-venue-field-bestfor" />
              </Field>
              <Field label="Description">
                <textarea className="lux-input" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="admin-venue-field-description" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Capacity">
                  <input className="lux-input" placeholder="150 – 1500" value={editing.capacity} onChange={(e) => setEditing({ ...editing, capacity: e.target.value })} data-testid="admin-venue-field-capacity" />
                </Field>
                <Field label="Indoor / Outdoor">
                  <select className="lux-select" value={editing.io} onChange={(e) => setEditing({ ...editing, io: e.target.value })} data-testid="admin-venue-field-io">
                    {IO_OPTS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Tier">
                  <select className="lux-select" value={editing.budget} onChange={(e) => setEditing({ ...editing, budget: e.target.value })} data-testid="admin-venue-field-budget">
                    {BUDGET_OPTS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Main image URL">
                <input className="lux-input" placeholder="https://…" value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} data-testid="admin-venue-field-image" />
              </Field>
              {editing.image && (
                <img src={editing.image} alt="preview" className="w-full h-44 object-cover rounded-xl" />
              )}
              <Field label="Extra image URLs (one per line)">
                <textarea
                  className="lux-input"
                  rows={2}
                  value={(editing.extra_images || []).join("\n")}
                  onChange={(e) => setEditing({ ...editing, extra_images: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                  data-testid="admin-venue-field-extras"
                />
              </Field>
              <Field label="Features (comma separated)">
                <input
                  className="lux-input"
                  value={(editing.features || []).join(", ")}
                  onChange={(e) => setEditing({ ...editing, features: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  data-testid="admin-venue-field-features"
                />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="CTA text">
                  <input className="lux-input" value={editing.cta_text} onChange={(e) => setEditing({ ...editing, cta_text: e.target.value })} data-testid="admin-venue-field-cta" />
                </Field>
                <Field label="Sort order">
                  <input type="number" className="lux-input" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} data-testid="admin-venue-field-sort" />
                </Field>
              </div>
              <label className="flex items-center gap-2 text-[14px] text-[#3B312D]">
                <input
                  type="checkbox"
                  checked={!!editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  data-testid="admin-venue-field-active"
                />
                Live on website
              </label>
            </div>
            <div className="flex justify-end gap-3 px-7 py-5 border-t border-[rgba(59,49,45,0.08)]">
              <button onClick={cancel} className="btn-ghost" data-testid="admin-venue-modal-cancel">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary" data-testid="admin-venue-modal-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {saving ? "Saving…" : (editing.id ? "Save changes" : "Create venue")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] tracking-[0.22em] uppercase text-[#8A9A7B] font-semibold mb-1.5">{label}</span>
    {children}
  </label>
);
